import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Collapse,
  CollapseProps,
  Divider,
  Flex,
  Form,
  List,
  Pagination,
  Popover,
  Select,
  Spin,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
  notification,
  theme,
} from "antd";
import {
  DownloadOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { utils, writeFile } from "xlsx";
import { axiosInstance, useFormatLastUpdate } from "@/utilities";
import { FormVerticalImportsContacts } from "@/components/vertical-form/importsContactsForm";
import { ContactsImportButton } from "@/components/upload-file-button/contactsImportButton";
import {
  API_URL_CONTACTS_IMPORT,
  API_URL_CONTACTS_IMPORT_CONFIG,
  API_URL_CONTACTS_IMPORT_FILE,
  API_URL_CONTACTS_IMPORT_ITEMS,
  API_URL_STORES,
} from "@/services/XCONS_ENV";

const { Text } = Typography;
const ACTIVE_STATUSES = ["creating", "pending"];

// Etiqueta legible de la condición IVA para el reporte (espeja BILLING_TYPE_MAP de la API).
const BILLING_LABELS: Record<number, string> = {
  1: "Consumidor Final",
  2: "Monotributo",
  3: "Empresa IVA Exento",
  4: "Responsable Inscripto",
  5: "Consumidor Final (recibe)",
};

interface StoreOption {
  code: string;
  name: string;
}

interface ImportContactsProps {
  /** Store preseleccionado (ej. el store filtrado en la lista de contactos). */
  defaultStoreCode?: string;
}

/**
 * Importador masivo de contactos (XLS). Se muestra embebido dentro de la sección
 * Contactos (drawer LateralModal), igual que el importador de productos vive
 * dentro de "Productos Asociados". Scopeado por Store: el operador confirma el
 * store, configura el mapeo de columnas del XLS y sube el archivo. El parseo del
 * XLS ocurre en el front (ContactsImportButton); el back crea/actualiza contactos
 * por (store_code, user_id).
 */
export const ImportContacts: React.FC<ImportContactsProps> = ({
  defaultStoreCode,
}) => {
  const { token } = theme.useToken();
  const formatLastUpdate = useFormatLastUpdate();
  const [form] = Form.useForm();

  const [stores, setStores] = useState<StoreOption[]>([]);
  const [storeCode, setStoreCode] = useState<string | undefined>(
    defaultStoreCode,
  );

  useEffect(() => {
    if (defaultStoreCode) setStoreCode(defaultStoreCode);
  }, [defaultStoreCode]);
  const [configData, setConfigData] = useState<Record<string, any>>({});
  const [imports, setImports] = useState<any[]>([]);
  const [loadingImports, setLoadingImports] = useState(false);
  const [activeKey, setActiveKey] = useState<string | string[]>([]);
  const [itemsByImport, setItemsByImport] = useState<Record<number, any[]>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const hasConfig = Object.keys(configData).length > 0;

  // ─── Stores ───────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const res = await axiosInstance.get(`${API_URL_STORES}?limit=1000`);
        const list = res.data?.data ?? res.data ?? [];
        setStores(
          list.map((s: any) => ({ code: s.code, name: s.name ?? s.code })),
        );
      } catch {
        notification.error({ message: "No se pudieron cargar los stores" });
      }
    })();
  }, []);

  // ─── Config de mapeo ────────────────────────────────────────────────────────
  const fetchConfig = useCallback(async (code: string) => {
    try {
      const res = await axiosInstance.get(
        `${API_URL_CONTACTS_IMPORT_CONFIG}/${code}`,
      );
      const data = res.data && Object.keys(res.data).length ? res.data : {};
      setConfigData(data);
      if (Object.keys(data).length) {
        form.setFieldsValue(data);
        setActiveKey([]);
      } else {
        form.resetFields();
        setActiveKey(["1"]); // sin config → abrir el mapeo
      }
    } catch {
      setConfigData({});
    }
  }, [form]);

  const fetchImports = useCallback(async (code: string) => {
    setLoadingImports(true);
    try {
      const res = await axiosInstance.get(
        `${API_URL_CONTACTS_IMPORT}/${code}?limit=1000&sortBy=createdAt:DESC`,
      );
      setImports(res.data?.data ?? []);
    } catch {
      setImports([]);
    } finally {
      setLoadingImports(false);
    }
  }, []);

  useEffect(() => {
    if (!storeCode) return;
    setItemsByImport({});
    fetchConfig(storeCode);
    fetchImports(storeCode);
  }, [storeCode, fetchConfig, fetchImports]);

  // Polling mientras haya imports procesándose.
  const hasActiveSyncs = useMemo(
    () => imports.some((r) => ACTIVE_STATUSES.includes(r.status)),
    [imports],
  );
  useEffect(() => {
    if (!hasActiveSyncs || !storeCode) return;
    const interval = setInterval(() => fetchImports(storeCode), 3000);
    return () => clearInterval(interval);
  }, [hasActiveSyncs, storeCode, fetchImports]);

  const handleSave = async (values: any) => {
    if (!storeCode) return;
    const payload = { ...values, storeCode, customColumns: values.customColumns ?? [] };
    try {
      if (hasConfig) {
        await axiosInstance.patch(
          `${API_URL_CONTACTS_IMPORT_CONFIG}/${storeCode}`,
          payload,
        );
      } else {
        await axiosInstance.post(`${API_URL_CONTACTS_IMPORT_CONFIG}`, payload);
      }
      notification.success({ message: "Configuración guardada" });
      setActiveKey([]);
      fetchConfig(storeCode);
    } catch {
      notification.error({ message: "Error al guardar la configuración" });
    }
  };

  const loadItems = async (importId: number) => {
    try {
      const res = await axiosInstance.get(
        `${API_URL_CONTACTS_IMPORT_ITEMS}/${importId}?limit=1000`,
      );
      setItemsByImport((prev) => ({ ...prev, [importId]: res.data?.data ?? [] }));
    } catch {
      setItemsByImport((prev) => ({ ...prev, [importId]: [] }));
    }
  };

  const downloadOriginal = async (importId: number, name: string) => {
    try {
      const res = await axiosInstance.get(
        `${API_URL_CONTACTS_IMPORT_FILE}/${importId}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || `import_contactos_${importId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      notification.error({ message: "Archivo original no disponible" });
    }
  };

  // Reporte descargable (XLSX) de lo procesado: una fila por contacto con su
  // resultado. Sirve para ambos carriles (Excel e Integración), incluso cuando
  // no hay archivo original (integración). Se arma en el front desde los items.
  const downloadReport = async (importId: number, name: string) => {
    try {
      const res = await axiosInstance.get(
        `${API_URL_CONTACTS_IMPORT_ITEMS}/${importId}?limit=1000`,
      );
      const items: any[] = res.data?.data ?? [];
      if (!items.length) {
        notification.info({ message: "No hay filas para reportar" });
        return;
      }
      const rows = items.map((it) => ({
        Nombre: it.firstname ?? "",
        Apellido: it.lastname ?? "",
        Email: it.email ?? "",
        WhatsApp: it.whatsapp ?? "",
        Grupo: it.group ?? "",
        // La empresa pasó a ser campo propio del item (va al campo Company del
        // contacto). El fallback a customFields cubre los imports viejos, de
        // cuando viajaba como columna personalizada.
        Empresa: it.company ?? it.customFields?.empresa ?? "",
        Comentario: it.customFields?.comentario ?? "",
        "Fact. Razón social": it.billingAddress?.company ?? "",
        "Fact. CUIT": it.billingAddress?.vatId ?? "",
        "Fact. Calle": it.billingAddress?.street1 ?? "",
        "Fact. CP": it.billingAddress?.postcode ?? "",
        "Fact. Cond. IVA":
          BILLING_LABELS[it.billingAddress?.billingType] ??
          it.billingAddress?.billingType ??
          "",
        Entrega: it.shippingAddress ? "sí" : "",
        Resultado: it.result ?? "",
        "Error / Nota": it.error ?? "",
      }));
      const ws = utils.json_to_sheet(rows);
      const wb = utils.book_new();
      utils.book_append_sheet(wb, ws, "Reporte");
      const base = (name || `import_${importId}`).replace(/\.(xlsx?|xls)$/i, "");
      writeFile(wb, `reporte_${base}.xlsx`);
    } catch {
      notification.error({ message: "No se pudo generar el reporte" });
    }
  };

  // ─── Tabla ────────────────────────────────────────────────────────────────
  const columns: TableColumnsType<any> = [
    {
      key: "date",
      title: "Fecha",
      dataIndex: "createdAt",
      render: (date) => <Flex style={{ minWidth: 180 }}>{formatLastUpdate(date)}</Flex>,
    },
    {
      key: "file",
      title: "Archivo",
      render: (_, record) => {
        const isIntegration = /\.(xlsx?|xls)$/i.test(record.name) === false;
        return (
          <Flex gap={12} align="center" style={{ minWidth: 220 }}>
            <Flex gap={8} vertical align="start">
              <Tag
                color={isIntegration ? "blue" : "green"}
                style={{ margin: 0, width: "fit-content" }}
              >
                {isIntegration ? "Integración" : "Excel"}
              </Tag>
              <Text style={{ color: token.colorTextSecondary }}>{record.name}</Text>
            </Flex>
            {record.fileKey && (
              <Tooltip title="Descargar original">
                <Button
                  icon={<DownloadOutlined />}
                  size="large"
                  onClick={() => downloadOriginal(record.importId, record.name)}
                />
              </Tooltip>
            )}
            {record.status === "completed" && (
              <Tooltip title="Descargar reporte">
                <Button
                  icon={<FileExcelOutlined />}
                  size="large"
                  onClick={() => downloadReport(record.importId, record.name)}
                />
              </Tooltip>
            )}
          </Flex>
        );
      },
    },
    {
      key: "status",
      title: "Reporte",
      dataIndex: "status",
      align: "end",
      render: (status, record) => {
        if (ACTIVE_STATUSES.includes(status)) {
          return (
            <Flex gap={8} justify="end" align="center">
              <LoadingOutlined />
              <Text>Procesando…</Text>
            </Flex>
          );
        }
        if (status === "completed") {
          return (
            <Flex vertical style={{ textAlign: "right" }}>
              <Flex gap={4} justify="end" align="center">
                <Badge status="success" />
                <Text>Completado</Text>
              </Flex>
              <Text style={{ color: token.colorTextSecondary, fontSize: token.fontSizeSM }}>
                Nuevos {record.created ?? 0} · Actualizados {record.updated ?? 0} · Sin cambios{" "}
                {record.notUpdated ?? 0} · Errores {record.errors ?? 0}
              </Text>
              {(record.validationErrors ?? 0) > 0 && (
                <Flex justify="end" style={{ marginTop: 4 }}>
                  <Popover
                    placement="topRight"
                    title={`${record.validationErrors} fila(s) con errores de validación`}
                    content={
                      <List
                        size="small"
                        style={{ maxHeight: 300, overflowY: "auto", minWidth: 280 }}
                        dataSource={record.errorDetails ?? []}
                        renderItem={(row: any) => (
                          <List.Item style={{ flexDirection: "column", alignItems: "flex-start", padding: "4px 0" }}>
                            <Text strong style={{ fontSize: token.fontSizeSM }}>
                              {row.rawKey || "(sin email/whatsapp)"}
                            </Text>
                            {row.errors?.map((e: any, i: number) => (
                              <Text key={i} style={{ color: token.colorError, fontSize: token.fontSizeSM }}>
                                • {e.field}: {e.message}
                              </Text>
                            ))}
                          </List.Item>
                        )}
                      />
                    }
                  >
                    <Tag color="warning" style={{ cursor: "pointer" }}>
                      {record.validationErrors} fila(s) ignoradas — ver detalle
                    </Tag>
                  </Popover>
                </Flex>
              )}
            </Flex>
          );
        }
        if (status === "error") {
          return (
            <Flex gap={4} justify="end" align="center">
              <Badge status="error" />
              <Text>Error</Text>
            </Flex>
          );
        }
        return <Badge status="default" text={status} />;
      },
    },
  ];

  const itemColumns: TableColumnsType<any> = [
    { key: "firstname", title: "Nombre", render: (_, r) => `${r.firstname ?? ""} ${r.lastname ?? ""}`.trim() },
    { key: "email", title: "Email", dataIndex: "email" },
    { key: "whatsapp", title: "WhatsApp", dataIndex: "whatsapp" },
    { key: "group", title: "Grupo", dataIndex: "group" },
    {
      key: "address",
      title: "Direcciones",
      render: (_, r) => (
        <Flex gap={4}>
          {r.shippingAddress && <Tag color="geekblue">Entrega</Tag>}
          {r.billingAddress && <Tag color="purple">Facturación</Tag>}
          {!r.shippingAddress && !r.billingAddress && (
            <Text style={{ color: token.colorTextSecondary }}>—</Text>
          )}
        </Flex>
      ),
    },
    {
      key: "result",
      title: "Resultado",
      dataIndex: "result",
      render: (result, r) => {
        const color =
          result === "created" ? "green" : result === "updated" ? "blue" : result === "error" ? "red" : "default";
        // `error` puede traer una nota de dirección ignorada aun en filas OK
        // (created/updated): se muestra como advertencia (naranja), no error.
        const isHardError = result === "error";
        return (
          <Flex vertical>
            <Tag color={color}>{result ?? "—"}</Tag>
            {r.error && (
              <Text
                style={{
                  color: isHardError ? token.colorError : token.colorWarning,
                  fontSize: token.fontSizeSM,
                }}
              >
                {r.error}
              </Text>
            )}
          </Flex>
        );
      },
    },
  ];

  const collapseItems: CollapseProps["items"] = [
    {
      key: "1",
      label: (
        <Flex gap={8} align="center">
          <Text>Configurar mapeo de columnas</Text>
          <Tooltip title="Indicá qué columna del XLS corresponde a cada campo del contacto">
            <InfoCircleOutlined style={{ color: token.colorIcon }} />
          </Tooltip>
        </Flex>
      ),
      children: (
        <FormVerticalImportsContacts
          formXlsProps={{ form }}
          handleSave={handleSave}
          closeColapse={() => setActiveKey([])}
        />
      ),
    },
  ];

  const paginatedData = imports.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  return (
    <Flex vertical style={{ flex: 1, overflow: "hidden" }}>
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
      <Flex vertical gap={16}>
      <Card>
        <Flex gap={12} align="center" wrap>
          <Text strong>Store:</Text>
          <Select
            showSearch
            allowClear
            style={{ minWidth: 280 }}
            placeholder="Elegí el store a importar"
            value={storeCode}
            onChange={(v) => setStoreCode(v)}
            optionFilterProp="label"
            options={stores.map((s) => ({ value: s.code, label: `${s.name} (${s.code})` }))}
          />
        </Flex>
      </Card>

      {storeCode && (
        <>
          <Card style={{ borderStyle: "dashed", backgroundColor: token.colorFillAlter }}>
            <Flex justify="flex-end" style={{ marginBottom: 20 }}>
              <ContactsImportButton
                formOk={hasConfig}
                configXlsData={{ data: configData }}
                storeCode={storeCode}
                refetchTableXls={() => fetchImports(storeCode)}
              />
            </Flex>
            {!hasConfig && (
              <Text type="warning">
                Configurá el mapeo de columnas antes de subir el XLS.
              </Text>
            )}
            <Divider />
            <Collapse
              ghost
              activeKey={activeKey}
              onChange={(k) => setActiveKey(k)}
              items={collapseItems}
            />
          </Card>

          <Spin spinning={loadingImports}>
            <Table
              className="table-bordered"
              rowKey="importId"
              scroll={{ x: true }}
              pagination={false}
              dataSource={paginatedData}
              columns={columns}
              expandable={{
                onExpand: (expanded, record) => {
                  if (expanded && !itemsByImport[record.importId]) {
                    loadItems(record.importId);
                  }
                },
                expandedRowRender: (record) => (
                  <Table
                    size="small"
                    rowKey="itemId"
                    pagination={{ pageSize: 10 }}
                    dataSource={itemsByImport[record.importId] ?? []}
                    columns={itemColumns}
                  />
                ),
              }}
              footer={() => (
                <Pagination
                  current={currentPage}
                  pageSize={pageSize}
                  total={imports.length}
                  onChange={(page, size) => {
                    setCurrentPage(page);
                    setPageSize(size);
                  }}
                  showTotal={(total) => `${total} importaciones`}
                />
              )}
            />
          </Spin>
        </>
      )}
      </Flex>
      </div>
    </Flex>
  );
};

export default ImportContacts;
